import { useLocalStorage } from '@mantine/hooks';
import { useRef, useState } from 'react';

export const STANDARD_SIDEBAR_WIDTH = 430;
const STORAGE_KEY = 'app-sidebar-width';

type Props = {
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export function useSidebarResize({ containerRef }: Props) {
  const [sidebarWidth, setSidebarWidth] = useLocalStorage({
    key: STORAGE_KEY,
    defaultValue: STANDARD_SIDEBAR_WIDTH,
    deserialize: (value: string | undefined) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : STANDARD_SIDEBAR_WIDTH;
    },
  });

  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!containerRef.current) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current || !isDraggingRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const maxWidth = containerRect.width * 0.5;
      const newWidth = containerRect.right - moveEvent.clientX;
      const clamped = Math.max(STANDARD_SIDEBAR_WIDTH, Math.min(maxWidth, newWidth));

      setSidebarWidth(clamped);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return { sidebarWidth, isDragging, handleMouseDown };
}
